/**
 * Google Drive Error Handler
 * Classifies errors and provides user-friendly messages
 */

export interface DriveErrorInfo {
  userMessage: string;
  errorCode: string;
  category: 'configuration' | 'network' | 'api' | 'auth' | 'validation';
  retryable: boolean;
  details?: string;
}

export class GoogleDriveErrorHandler {
  static classifyError(error: any): DriveErrorInfo {
    if (!error) {
      return {
        userMessage: 'An unknown error occurred',
        errorCode: 'unknown_error',
        category: 'api',
        retryable: false
      };
    }

    const status = error.status || error.response?.status;
    const message = error.message || error.detail || '';

    if (error.name === 'TypeError' && (message.includes('fetch') || message.includes('network'))) {
      return {
        userMessage: 'Network error. Check your internet connection and try again.',
        errorCode: 'network_error',
        category: 'network',
        retryable: true,
        details: message
      };
    }

    if (error.name === 'AbortError' || message.includes('timeout')) {
      return {
        userMessage: 'Request timed out. Please try again.',
        errorCode: 'timeout',
        category: 'network',
        retryable: true,
        details: message
      };
    }

    switch (status) {
      case 401:
        return {
          userMessage: 'Google Drive access expired. Please reconnect in Settings > Integrations.',
          errorCode: 'auth_expired',
          category: 'auth',
          retryable: false,
          details: message
        };

      case 403:
        return {
          userMessage: 'Permission denied. The file may be private or you lack access.',
          errorCode: 'permission_denied',
          category: 'auth',
          retryable: false,
          details: message
        };

      case 404:
        return {
          userMessage: 'File not found. It may have been deleted or moved.',
          errorCode: 'file_not_found',
          category: 'validation',
          retryable: false,
          details: message
        };

      case 409:
        return {
          userMessage: 'Sync already in progress. Please wait for it to complete.',
          errorCode: 'sync_in_progress',
          category: 'validation',
          retryable: true,
          details: message
        };

      case 413:
        return {
          userMessage: 'File too large. Maximum size is 50MB.',
          errorCode: 'file_too_large',
          category: 'validation',
          retryable: false,
          details: message
        };

      case 429:
        return {
          userMessage: 'Google Drive API rate limit reached. Please wait a few minutes and try again.',
          errorCode: 'rate_limit',
          category: 'api',
          retryable: true,
          details: message
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          userMessage: 'Google Drive is temporarily unavailable. Please try again later.',
          errorCode: 'service_unavailable',
          category: 'api',
          retryable: true,
          details: message
        };
    }

    if (message.toLowerCase().includes('not connected')) {
      return {
        userMessage: 'Google Drive is not connected. Please connect it in Settings > Integrations.',
        errorCode: 'not_connected',
        category: 'configuration',
        retryable: false,
        details: message
      };
    }

    if (message.toLowerCase().includes('unsupported file type')) {
      return {
        userMessage: 'This file type cannot be read. Try using gdrive_import for images or other binary files.',
        errorCode: 'unsupported_file_type',
        category: 'validation',
        retryable: false,
        details: message
      };
    }

    if (message.toLowerCase().includes('integration not found')) {
      return {
        userMessage: 'Google Drive integration not found. Please reconnect in Settings.',
        errorCode: 'integration_not_found',
        category: 'configuration',
        retryable: false,
        details: message
      };
    }

    return {
      userMessage: message || 'An unexpected error occurred. Please try again.',
      errorCode: 'unknown_error',
      category: 'api',
      retryable: false,
      details: message
    };
  }

  static logError(context: string, error: any, additionalInfo?: Record<string, any>): void {
    const errorInfo = this.classifyError(error);
    
    console.error(`[${context}] Error:`, {
      code: errorInfo.errorCode,
      category: errorInfo.category,
      retryable: errorInfo.retryable,
      message: errorInfo.userMessage,
      details: errorInfo.details,
      ...additionalInfo
    });
  }
}
